import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import tvTheme, { tvBroadcastBezel, tvTextStroke } from './tvTheme';
import TVQrOverlay from './TVQrOverlay';

function TVFrameCard({
  frameNumber,
  frameTitle,
  children,
  compact = false,
  broadcast = false,
  showBadge = true,
  allowOverflow = false,
  showQrOverlay = true,
  onClick,
  selected = false,
}) {
  const showInnerBadge = showBadge && !broadcast && frameNumber != null;

  const screenSx = {
    position: 'relative',
    width: '100%',
    aspectRatio: tvTheme.frame.aspectRatio,
    borderRadius: broadcast ? 0 : tvTheme.frame.borderRadius,
    overflow: allowOverflow ? 'visible' : 'hidden',
    background: tvTheme.colors.skyGradient,
    border: broadcast
      ? '2px solid #0a0a0a'
      : selected
        ? `4px solid ${tvTheme.colors.gold}`
        : tvTheme.frame.border,
    boxShadow: broadcast ? 'none' : tvTheme.frame.shadow,
    cursor: onClick ? 'pointer' : 'default',
    transition: broadcast ? undefined : 'transform 0.2s, box-shadow 0.2s',
    '&:hover': onClick && !broadcast
      ? { transform: 'scale(1.008)', boxShadow: '0 10px 28px rgba(13,74,143,0.4)' }
      : {},
  };

  const inner = (
    <Box
      sx={screenSx}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {showInnerBadge && (
        <Box
          sx={{
            position: 'absolute',
            top: compact ? 6 : 10,
            left: compact ? 6 : 10,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            px: compact ? 0.75 : 1,
            py: compact ? 0.3 : 0.45,
            borderRadius: '6px',
            bgcolor: 'rgba(255,255,255,0.92)',
            border: '2px solid #fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: compact ? '0.7rem' : '0.8rem',
              fontWeight: 900,
              color: tvTheme.colors.titleOrange,
              ...tvTextStroke('#fff'),
            }}
          >
            {String(frameNumber).padStart(2, '0')}
          </Typography>
          {frameTitle && (
            <Typography
              component="span"
              sx={{
                fontSize: compact ? '0.72rem' : '0.85rem',
                fontWeight: 800,
                color: tvTheme.colors.textDark,
              }}
            >
              {frameTitle}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          p: compact ? 0.3 : 0.28,
          pt: showInnerBadge ? (compact ? 2.5 : 2.35) : compact ? 0.3 : 0.28,
          overflow: allowOverflow ? 'visible' : 'hidden',
        }}
      >
        {children}
      </Box>
      {showQrOverlay && <TVQrOverlay compact={compact} />}
    </Box>
  );

  if (broadcast) {
    return (
      <Box
        sx={{
          ...tvBroadcastBezel,
          width: '100%',
          ...(selected ? { outline: `3px solid ${tvTheme.colors.gold}`, outlineOffset: 4 } : {}),
        }}
      >
        {inner}
      </Box>
    );
  }

  return inner;
}

TVFrameCard.propTypes = {
  frameNumber: PropTypes.number,
  frameTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  compact: PropTypes.bool,
  broadcast: PropTypes.bool,
  showBadge: PropTypes.bool,
  allowOverflow: PropTypes.bool,
  showQrOverlay: PropTypes.bool,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};

export default TVFrameCard;
